import React, {useEffect, useRef, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {SettingsBreadCrum} from '../core/_constants'
import {RankingInfo, rankItem} from '@tanstack/match-sorter-utils'
import {useIntl} from 'react-intl'
import threeDots from '../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import {InfoCard2} from '../../widgets/components/UI/InfoCard2'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {Pagination, Table} from 'react-bootstrap'
import SkeletonRow from '../../digital-signing/components/SkeletonRow'
import NoDataPage from '../../widgets/components/NoDataPage'
import {DisplayImage} from '../../widgets/components/General/DisplayImage'
import NewCompanyModal from './NewCompanyModal'
import {getAllCompany} from '../../investor-crm/core/_requests'
import {useAuth} from '../../auth'
import {useTimeZone} from '../../../hooks/useTimeZone'
import {useDateFormat} from '../../../hooks/useDateFormat'
import {DateTime} from 'luxon'
import {saveAs} from 'file-saver'
import {companyData, updateUserDefaultCompany} from '../../profile/core/_requests'
import {getUserByToken, refreshTokenApi} from '../../auth/core/_requests'
import SwitchingCompany from './SwitchingCompany'

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({itemRank})
  return itemRank.passed
}

const MyCompanies = () => {
  const {formatMessage} = useIntl()
  const [loading, setLoading] = useState(false) //eslint-disable-line
  const [isActive, setIsActive] = useState(false)
  const [isActiveSwitching, setIsActiveSwitching] = useState(false)
  const [showKebabMenu, setShowKebabMenu] = useState(false)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [expandedYourShareholdings, setExpandedYourShareholdings] = useState<number | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [companyList, setCompanyList] = useState<any[]>([])
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 10,
  })
  const [search, setSearch] = useState('') //eslint-disable-line
  const {getTimeZoneValue} = useTimeZone()
  const {getDateValue} = useDateFormat()
  // const storedCompany = localStorage.getItem('company')
  // const company = storedCompany ? JSON.parse(storedCompany) : null
  const storedCompanyId = localStorage.getItem('companyId')
  const [isLoading, setIsLoading] = useState<boolean>(false) // eslint-disable-line

  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowKebabMenu(false)
    }
  }
  const {
    companyId,
    storeCompanyId,
    currentUser,
    setRole,
    setCompanyQueuePosition,
    setUseCase,
    setInvoiceStatus,
    setPlanStatus,
    setUserToken,
    saveAuth,
  } = useAuth()

  const sortCompaniesBySelected = (companies: any[], companyId: number) => {
    return companies.slice().sort((a, b) => {
      if (a.companyId === companyId) return -1
      if (b.companyId === companyId) return 1
      return 0
    })
  }
  useEffect(() => {
    if (companyId) {
      getAllCompanies()
    }
  }, [companyId]) // eslint-disable-line

  const getAllCompanies = async () => {
    setLoading(true)
    const selectedCompanyId = storedCompanyId ? +storedCompanyId! : +companyId!
    try {
      const {
        data: {success, data},
      } = await getAllCompany(pagination.pageSize, search, true)
      if (success) {
        const sorted = sortCompaniesBySelected(data, +selectedCompanyId!)
        setCompanyList(sorted)
      } else {
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleYourRowToggle = (rowIndex: number) => {
    setExpandedYourShareholdings(expandedYourShareholdings === rowIndex ? null : rowIndex)
  }

  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        accessorKey: 'companyId',
        id: 'companyId',
        header: 'Company ID',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'name',
        id: 'name',
        header: 'Company Name',
        cell: (info) => {
          const fileName = info.row?.original?.file?.name || ''
          return (
            <div className='d-flex align-items-center gap-2'>
              <DisplayImage
                imgName={fileName}
                alt={'Company-Logo'}
                width={24}
                className='rounded'
              />
              <div>
                <h5 className='m-0 fw-normal font-size-13'>
                  {info.getValue().length > 20
                    ? info.getValue().slice(0, 20) + '...'
                    : info.getValue()}
                </h5>
              </div>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorFn: (row) => row?.owner?.country?.countryName || '-',
        id: 'country',
        header: 'Country',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'industry',
        id: 'industry',
        header: 'Industry',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
      {
        accessorFn: (row) => row?.roles?.roleName || '',
        id: 'yourRole',
        header: 'Your Role',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
      {
        accessorKey: 'createdAt',
        id: 'createdAt',
        header: 'Created On',
        cell: (info) => {
          const dateString = (info.getValue() as string)?.toString()
          return (
            <>
              {dateString
                ? getDateValue(dateString)
                  ? getDateValue(dateString)
                  : DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_MED)
                : '-'}{' '}
              {dateString && getTimeZoneValue(dateString)}
            </>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorFn: (row) => row?.owner?.firstName + ' ' + row?.owner?.lastName || '',
        id: 'owner',
        header: 'Owned By',
        cell: (info) => info.getValue(),
        filterFn: 'includesString',
      },
    ],[]) // eslint-disable-line react-hooks/exhaustive-deps
    
  const table = useReactTable({
    data: companyList,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      pagination,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    globalFilterFn: 'fuzzy',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })
  // console.log(companyList, 'COmmmmmmmmmmmmmmmmmmmmmm')
  const pageCount = Math.ceil(table.getPrePaginationRowModel().rows.length / pagination.pageSize) //eslint-disable-line
  const currentPage = table.getState().pagination.pageIndex //eslint-disable-line

  const handlePageChange = (pageIndex: number) => {
    //eslint-disable-line
    table.setPageIndex(pageIndex)
  }
  const exportToCSV = () => {
    const rows = table.getFilteredRowModel().rows
    const headers = table
      .getAllColumns()
      .filter((col) => col.getIsVisible())
      .map((col) => col.columnDef.header)

    const data = rows.map((row) => row.getVisibleCells().map((cell) => cell.getValue()))

    const csv = [headers.join(','), ...data.map((row) => row.join(','))].join('\n')

    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'})
    saveAs(blob, 'companies.csv')
  }

  const refreshToken = async (itemCompany: number) => {
    try {
      const {
        data: {success, data},
      } = await refreshTokenApi()
      if (success) {
        await saveAuth(data)
        setUserToken(data.token)

        // setCurrentUser({ ...value })
        // setUserToken(data.token)
        if(data?.companyPlanStatus?.invoiceStatus){
          setInvoiceStatus(data.companyPlanStatus.invoiceStatus)
        }
        if(data?.companyPlanStatus?.planStatus){
          setPlanStatus(data.companyPlanStatus.planStatus)
        }

        const {data: userData} = await getUserByToken(data.token)
        if (userData?.data) {
          const findCompany = userData?.data?.company?.find(
            (company: {companyId: number}) => company.companyId === itemCompany
          )
          if (findCompany) {
            setCompanyQueuePosition(findCompany?.queuePosition)
            setUseCase(findCompany?.useCase)
          }
        }
      }
    } catch (error) {
      console.log(error)
    }
  }

  const onCompanySelect = async (item: any) => {
    setIsLoading(true)
    try {
      if (item) {
        const findCompany = currentUser?.company.find(
          (comp: {companyId: number}) => comp.companyId === item.companyId
        )
        if (findCompany) {
          // if (isRecordskeeper) {
          setRole(findCompany?.roles?.roleName?.toLowerCase())
          // } else {
          //   setRole(findCompany?.roles?.user_role.roleId)
          // }
        }
        const {
          data: {success},
        }: any = await updateUserDefaultCompany(companyId!, item.companyId)
        if (success) {
          const {
            data: {success},
          } = await companyData(item.companyId)
          if (success) {
            storeCompanyId(item.companyId)
            setCompanyQueuePosition(item?.queuePosition)
            localStorage.setItem('company', JSON.stringify(item))
            localStorage.setItem('companyId', item.companyId)
            refreshToken(item.companyId)
            if (`${window.location.hostname}` === 'localhost') {
              window.location.href = `${window.location.protocol}//${window.location.hostname}:3003/dashboard`
            } else {
              window.location.href = `${window.location.protocol}//${window.location.hostname}/dashboard`
            }
          } else {
          }
        }
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }
  const [loadingCompanyId, setLoadingCompanyId] = useState(null)
  return (
    <>
      <div>
        <PageTitle
          breadcrumbs={SettingsBreadCrum}
          description={formatMessage({id: 'My Companies'})}
        >
          {formatMessage({id: 'My Companies'})}
        </PageTitle>
      </div>

      <div className='px-6'>
        <InfoCard2
          title={'Companies You’re Associated With'}
          desc={`This section displays all the companies you're currently associated with on RecordsKeeper.AI. Each company listed here represents a workspace where you collaborate, manage records, and maintain compliance.`}
          slug={
            'https://help.recordskeeper.ai/en/articles/11059784-managing-your-companies-in-recordskeeper-ai-complete-guide'
          }
        />
        <div className='d-flex flex-md-row flex-column align-items-md-center gap-2 mb-4'>
          <div className='d-flex flex-md-row flex-column justify-content-between align-items-md-center align-items-start w-100 gap-md-1 gap-2 '>
            <h1 className='mb-0 font-size-16'>{formatMessage({id: 'My Companies'})}</h1>
            {/* <div className='h-100 border bg-white d-flex rounded'>
                            <button
                                className={clsx(
                                    'text-clr88 btn btn-sm btn-flex fw-semibold py-2 font-size-12 height-36'
                                )}
                                id='fc-toogle'
                                title='Sort and Filter'
                                data-bs-toggle='tooltip'
                                data-bs-placement='left'
                                data-bs-dismiss='click'
                                data-bs-trigger='hover'
                                onClick={() => {
                                }}
                            >
                                <KTSVG
                                    path={toAbsoluteUrl('/media/icons/duotune/general/gen031.svg')}
                                    className='svg-icon-6 svg-icon-muted me-1'
                                />
                                <span className='d-none d-md-block'>{formatMessage({ id: 'Filter' })}</span> */}
            {/* {selectedShareholderTypeFilter || selectedShareholderSortTypeFilter ? '*' : ''} */}
            {/* </button> */}
            {/* </div> */}
            <form className='align-items-center w-100 w-md-auto d-flex'>
              <span className='svg-icon svg-icon-3 position-absolute ms-3'>
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className='mh-50px'
                >
                  <rect
                    opacity='0.5'
                    x='17.0365'
                    y='15.1223'
                    width='8.15546'
                    height='2'
                    rx='1'
                    transform='rotate(45 17.0365 15.1223)'
                    fill='currentColor'
                  ></rect>
                  <path
                    d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                    fill='currentColor'
                  ></path>
                </svg>
              </span>
              <input
                className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400 height-36'
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder='Search Company'
              />
            </form>
          </div>
          <div className='d-flex gap-md-1 gap-1 flex-shrink-0'>
            <button
              className='btn text-white custom-button-property bg-primary height-36 d-flex align-items-center font-size-12 w-100 justify-content-center '
              onClick={() => {
                setIsActive(true)
              }}
            >
              {formatMessage({id: 'Add New Company'})}
            </button>
            <NewCompanyModal
              isActive={isActive}
              setIsActive={setIsActive}
              getAllCompanies={getAllCompanies}
            />
            <div className='position-relative align-self-top height-36'>
              <div
                className='p-3 cursor-pointer bg-white rounded-2 d-flex justify-content-center align-items-center height-36'
                onClick={() => {
                  setShowKebabMenu(!showKebabMenu)
                }}
              >
                <img src={threeDots} alt='' width={16} height={16} />
              </div>
              {showKebabMenu && (
                <div
                  className='card shadow rounded top-4 cursor-pointer z-index-1 position-absolute w-150px'
                  style={{top: '40px', right: 0}}
                  ref={dropdownRef}
                >
                  <div className='rounded p-2 dropdown-items w-100 bg-hover-light'>
                    <button
                      type='button'
                      className='m-0 border-0 bg-transparent text-dark font-size-12 bg-hover-muted'
                      onClick={() => exportToCSV()}
                    >
                      {formatMessage({id: 'Export to CSV'})}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='bg-white rounded-top p-4' style={{overflowX: 'auto'}}>
          {!loading && table.getRowModel().rows.length === 0 ? (
            <div className='my-5 mx-6'>
              <NoDataPage />
            </div>
          ) : (
            <Table className='w-100 mb-0' style={{minWidth: '600px'}}>
              <thead className='custom-thead border-bottom'>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className='font-size-12 font-weight-400 table-row border-dark-mode'
                  >
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        className='min-w-120px pb-6 pt-0 px-4'
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            className={
                              header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                            }
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {{
                              asc: ' ▲',
                              desc: ' ▼',
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>
                        )}
                      </th>
                    ))}
                    <th className='min-w-120px pb-6 pt-4 text-end px-4'>
                      {formatMessage({id: 'Actions'})}
                    </th>
                  </tr>
                ))}
              </thead>
              <tbody>
                {loading ? (
                  <>
                    <SkeletonRow count={8} />
                  </>
                ) : (
                  table.getRowModel().rows.map((row, index) => (
                    <React.Fragment key={row.id}>
                      <tr
                        // className='border-bottom'
                        key={row.id}
                        onClick={() => handleYourRowToggle(index)}
                        className={`border-bottom bg-white`}
                      >
                        {row.getVisibleCells().map((cell, cellIndex) => (
                          <>
                            {cellIndex === 0 ? (
                              <td
                                key={cell.id}
                                className='pt-5 px-4 border-0'
                                style={{minWidth: '70px'}}
                              >
                                <div className='d-flex align-items-center gap-2'>
                                  {/* <p
                                    className='m-0'
                                    style={{
                                      transform:
                                        expandedYourShareholdings === index
                                          ? 'rotate(90deg)'
                                          : 'rotate(0deg)',
                                      transition: 'transform 0.3s ease',
                                    }}
                                  >
                                    &gt;
                                  </p> */}
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </div>
                              </td>
                            ) : cellIndex === 4 ? (
                              <td key={cell.id} className='pt-4 px-4 border-0'>
                                <div
                                  className='d-flex justify-content-center align-items-center gap-2 badge bg-success bg-opacity-25 text-success py-2'
                                  style={{width: '100px'}}
                                >
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </div>
                              </td>
                            ) : (
                              <td key={cell.id} className={`pt-5 px-4 border-0`}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            )}
                          </>
                        ))}
                        <td
                          className='pt-4 px-4 text-end'
                          onClick={(e) => {
                            e.stopPropagation()
                            // setMemoryId(row.original.memoryId)
                          }}
                        >
                          {companyId && companyId === row.original.companyId ? (
                            <div className='text-success d-flex align-items-center gap-2 justify-content-end'>
                              <img
                                src={toAbsoluteUrl('/media/icons/duotune/general/ic-correct.svg')}
                                alt=''
                              />
                              <p className='mb-0 text-nowrap'>
                                {formatMessage({id: 'Current Company'})}
                              </p>
                            </div>
                          ) : (
                            <>
                              <div
                                className='text-primary d-flex align-items-center gap-2 justify-content-end cursor-pointer'
                                onClick={() => {
                                  setLoadingCompanyId(row.original.companyId)
                                  setIsActiveSwitching(true)
                                  onCompanySelect(row.original)
                                }}
                              >
                                <img
                                  src={toAbsoluteUrl('/media/icons/duotune/general/ic-switch.svg')}
                                  alt=''
                                />
                                <p className='mb-0 text-nowrap'>
                                  {loadingCompanyId === row.original.companyId
                                    ? 'Please wait...'
                                    : 'Switch Company'}
                                </p>
                              </div>
                              <SwitchingCompany
                                isActive={isActiveSwitching}
                                setIsActive={setIsActiveSwitching}
                              />
                            </>
                          )}
                        </td>
                      </tr>
                      {/* {expandedYourShareholdings === index && row.original && (
                        <>
                          <tr
                            className={`${expandedYourShareholdings === index
                              ? 'expanded-row-captable bg-light'
                              : 'bg-white'
                              }`}
                          >
                            <td className='min-w-120px pb-6 pt-4 ps-4'>
                              <p className='ms-4'>{formatMessage({ id: 'Owned By' })}</p>
                              <p className='font-size-12 ms-4 text-muted'>
                                {row.original.owner
                                  ? `${row.original.owner?.firstName} ${row.original.owner?.lastName}`
                                  : formatMessage({ id: '-' })}
                              </p>
                            </td>
                            <td className='min-w-120px pb-6 pt-4 ps-4'>
                              <p className=''>{formatMessage({ id: 'Country' })}</p>
                              <p className='font-size-12 text-muted'>
                                {row.original?.owner?.country?.countryName ||
                                  formatMessage({ id: '-' })}
                              </p>
                            </td>
                            <td colSpan={3} className='min-w-120px pb-6 pt-4 ps-4'>
                              <p className=''>{formatMessage({ id: 'Industry' })}</p>
                              <p className='font-size-12 text-muted'>
                                {row.original?.industry ||
                                  formatMessage({ id: '-' })}
                              </p>
                            </td>
                          </tr>
                        </>
                      )} */}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </div>
        <div className='w-100 d-md-flex justify-content-between align-items-center px-4 py-4 bg-white rounded-bottom mb-8'>
          <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-4 mb-md-0'>
            <select
              aria-label='Default select example'
              className='p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-white text-dark '
              style={{outline: 'none'}}
              value={pagination.pageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value)
                setPagination((prev) => ({
                  ...prev,
                  pageSize: newSize,
                  pageIndex: 0, // Reset to first page when page size changes
                }))
              }}
            >
              {[10, 20, 50, 100].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>

            <span className='font-size-13 text-center text-md-start'>
              {formatMessage({
                id: `Showing ${currentPage * pagination.pageSize + 1} to ${Math.min(
                  (currentPage + 1) * pagination.pageSize,
                  table.getPrePaginationRowModel().rows.length
                )} of ${table.getPrePaginationRowModel().rows.length} records`,
              })}
            </span>
          </div>

          {/* <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
                        <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0}>
                            {'<<'}
                        </Pagination.First>

                        <Pagination.Prev
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 0}
                        >
                            {'<'}
                        </Pagination.Prev>

                        {Array.from({ length: pageCount }, (_, i) => (
                            <Pagination.Item
                                key={i}
                                active={i === currentPage}
                                onClick={() => handlePageChange(i)}
                            >
                                {i + 1}
                            </Pagination.Item>
                        ))}

                        <Pagination.Next
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= pageCount - 1}
                        >
                            {'>'}
                        </Pagination.Next>

                        <Pagination.Last
                            onClick={() => handlePageChange(pageCount - 1)}
                            disabled={currentPage >= pageCount - 1}
                        >
                            {'>>'}
                        </Pagination.Last>
                    </Pagination> */}
          <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
            <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0}>
              {'<<'}
            </Pagination.First>

            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0}
            >
              {'<'}
            </Pagination.Prev>

            {pageCount > 5 ? (
              <>
                {currentPage > 1 && (
                  <>
                    <Pagination.Item onClick={() => handlePageChange(0)}>1</Pagination.Item>
                    {currentPage > 2 && <Pagination.Ellipsis disabled />}
                  </>
                )}

                {[
                  currentPage === 0 ? 0 : currentPage - 1,
                  currentPage,
                  currentPage === pageCount - 1 ? pageCount - 1 : currentPage + 1,
                ].map((i) => (
                  <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                  >
                    {i + 1}
                  </Pagination.Item>
                ))}

                {currentPage < pageCount - 2 && (
                  <>
                    <Pagination.Ellipsis disabled />
                    <Pagination.Item onClick={() => handlePageChange(pageCount - 1)}>
                      {pageCount}
                    </Pagination.Item>
                  </>
                )}
              </>
            ) : (
              Array.from({length: pageCount}, (_, i) => (
                <Pagination.Item
                  key={i}
                  active={i === currentPage}
                  onClick={() => handlePageChange(i)}
                >
                  {i + 1}
                </Pagination.Item>
              ))
            )}

            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= pageCount - 1}
            >
              {'>'}
            </Pagination.Next>

            <Pagination.Last
              onClick={() => handlePageChange(pageCount - 1)}
              disabled={currentPage >= pageCount - 1}
            >
              {'>>'}
            </Pagination.Last>
          </Pagination>
        </div>
      </div>
    </>
  )
}

export default MyCompanies
